import React, { useEffect } from 'react';
import { autoId, formFieldErrorPropsV2 } from '@workday/upc-common';
import { PrimaryButton, TertiaryButton } from '@workday/canvas-kit-react/button';
import { useForm } from 'react-hook-form';
import { ACCOUNT_DEFAULT_PASSWORD } from '../../../../nx-environment-variables';
import { ID_CANCEL_BUTTON, ID_SUBMIT_BUTTON, ID_PASSWORD } from '../../../../autoIds';
import { useVerifyPasswordToUpdatePhoneMutation } from '../../../../slices/account-api.slice';
import { useTranslation } from 'react-i18next';
import { FormField } from '@workday/canvas-kit-react';
import styles from '../../../../feature-auth/login/loginPasswordEntry/LoginPasswordEntry.module.scss';
import { PasswordTextInput } from '../../../../shared/password-input/password-input';
import { PasswordFormState } from '../../../../slices/types';
import { serverError } from '../../../../utils/server-error';
import { useErrorHandling } from '../../../../hooks/useErrorHandling';

interface ManagePhoneNumbersVerifyPasswordProps {
    onNext: () => void;
    handleDone: () => void;
}

export function ManagePhoneNumbersVerifyPassword({ onNext, handleDone }: ManagePhoneNumbersVerifyPasswordProps) {
    const [verifyPasswordToUpdatePhone, verifyPasswordToUpdatePhoneMutation] = useVerifyPasswordToUpdatePhoneMutation();

    const { t } = useTranslation('account');

    const { handleSubmit, register, formState, setError } = useForm<PasswordFormState>({
        defaultValues: {
            password: ACCOUNT_DEFAULT_PASSWORD || ''
        }
    });

    useEffect(() => {
        if (verifyPasswordToUpdatePhoneMutation.isSuccess) {
            onNext();
        }
    }, [onNext, verifyPasswordToUpdatePhoneMutation]);

    useErrorHandling(verifyPasswordToUpdatePhoneMutation.error, {
        inlineErrors: ['UPC-1000', 'UPC-1017', 'UPC-1054', 'UPC-1056', 'UPC-1066'],
        onInlineError: error => setError('password', serverError(error))
    });

    function onSubmit(values) {
        verifyPasswordToUpdatePhone({ password: values.password });
    }

    function onCancel() {
        handleDone();
    }

    return (
        <div className='settingsModalContent'>
            <form onSubmit={handleSubmit(onSubmit)}>
                <h1>{t('ManagePhoneNumbersVerifyPassword.title')}</h1>
                <h2>{t('ManagePhoneNumbersVerifyPassword.subTitle')}</h2>
                <br />
                <FormField
                    className={styles.formField}
                    grow={true}
                    label={t('ManagePhoneNumbersVerifyPassword.passwordFieldLabel')}
                    id='password'
                    {...formFieldErrorPropsV2(
                        formState.errors.password,
                        undefined,
                        t('Required.fieldErrorLabel', { ns: 'shared' })
                    )}>
                    <PasswordTextInput
                        {...autoId(ID_PASSWORD)}
                        autoFocus={true}
                        autoComplete='current-password'
                        disabled={verifyPasswordToUpdatePhoneMutation.isLoading}
                        // @ts-ignore
                        placeholder={t('ManagePhoneNumbersVerifyPassword.passwordInputPlaceholder')}
                        {...register('password', {
                            required: t('Required.fieldGeneric', { ns: 'shared' })
                        })}
                    />
                </FormField>
                <div className='settingsModalContent__footer'>
                    <PrimaryButton
                        {...autoId(ID_SUBMIT_BUTTON)}
                        type='submit'
                        disabled={verifyPasswordToUpdatePhoneMutation.isLoading}
                        size='large'
                        grow={true}>
                        {t('ManagePhoneNumbersVerifyPassword.submitButton')}
                    </PrimaryButton>
                    <br />
                    <div className='settingsModalContent__cancel'>
                        <TertiaryButton {...autoId(ID_CANCEL_BUTTON)} type='button' size='large' onClick={onCancel}>
                            {t('Common.cancel', { ns: 'shared' })}
                        </TertiaryButton>
                    </div>
                </div>
            </form>
        </div>
    );
}
